/**
 * Context-delivery outcome for a sent chat turn (selection + @-mention
 * directives), extracted so the decision is pinnable without a DOM.
 *
 * ## Why this exists — the durably-affirmed context loss (F2, widened)
 *
 * The selection/mentions directives ride `runtime.system.enqueue_event`
 * BEFORE `onSend`. Before this seam, the composer cleared the chips AND
 * threaded the selection echo onto the sent message UNCONDITIONALLY — so
 * whenever the enqueue did not happen (org-scope chat: the endpoint needs
 * case ids) or FAILED (any transport blip, case scope included — the catch
 * degrades by design), the sent bubble still rendered the Quote echo for a
 * selection the model never received. The UI did not merely stay silent
 * about the loss; it affirmed delivery. (`test-suite`'s F2, widened
 * 2026-08-24: ANY failed enqueue does it, and the mentions path shares the
 * shape.)
 *
 * The rule that fixes it: **the signal keys on "did THIS enqueue actually
 * succeed" — knowable at the scope guard AND at the catch — never on which
 * scope we are in.** A scope check alone would miss the case-scope
 * transport failure entirely.
 *
 * Consumers: `ChatInput.handleSend` threads the echo(es) onto the message
 * ONLY for delivered context, and raises one warn toast for the rest.
 * Note the durable half was already honest — the runtime persists a
 * selection only when it actually received the directive, so reload never
 * showed the forged echo. This aligns the LIVE render with that truth.
 */

export type ContextUndelivered = "selection" | "mentions" | "both" | null;

export function contextUndeliveredKind(args: {
  /** A selection chip was attached to this send. */
  selectionSent: boolean;
  /** The selection enqueue RESOLVED OK (false = skipped or threw). */
  selectionDelivered: boolean;
  /** One or more mention chips were attached to this send. */
  mentionsSent: boolean;
  /** The mentions enqueue RESOLVED OK (false = skipped or threw). */
  mentionsDelivered: boolean;
}): ContextUndelivered {
  const sel = args.selectionSent && !args.selectionDelivered;
  const men = args.mentionsSent && !args.mentionsDelivered;
  if (sel && men) return "both";
  if (sel) return "selection";
  if (men) return "mentions";
  return null;
}
